import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import type {
  BookingResponse,
  BookingWithOptionsResponse,
  BookingOptionResponse,
  CreateBookingOptionRequest,
  UpdateBookingOptionRequest,
  UpdateBookingStatusRequest,
  CancelBookingRequest,
  ConfirmVendorBookingRequest,
  BookingDetailResponse,
  BookingOptionsListResponse
} from '@/types/activities';

interface UseBookingsParams {
  organizationId?: string;
  status?: string;
  priority?: string;
  requiresAttention?: boolean;
}

// Main bookings list hook for dashboard
export function useBookings(params: UseBookingsParams = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['bookings', params],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        // TODO: Replace with actual endpoint when available - using placeholder for now
        const queryParams = new URLSearchParams();
        if (params.organizationId) queryParams.append('organizationId', params.organizationId);
        if (params.status) queryParams.append('status', params.status);
        if (params.priority) queryParams.append('priority', params.priority);
        if (params.requiresAttention !== undefined) queryParams.append('requiresAttention', params.requiresAttention.toString());

        const url = `/bookings${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
        // apiRequest unwraps the API envelope and returns the `.data` payload — the
        // /bookings endpoint returns BookingResponse[] (full booking records).
        const response = await apiRequest<BookingResponse[]>("GET", url);

        // Ensure we always return an array, even if the API response is undefined or null
        return Array.isArray(response) ? response : [];
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch bookings';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Single booking detail with options
export function useBooking(bookingId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `/bookings/${bookingId}`;
        const response = await apiRequest<BookingDetailResponse>("GET", url);

        // Handle different response structures
        // API might return { data: booking } or just booking directly
        const bookingData = response?.data || response;

        if (!bookingData) {
          throw new Error('No booking data returned from API');
        }

        return bookingData;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch booking details';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!bookingId
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Booking options for a specific booking
export function useBookingOptions(bookingId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['bookingOptions', bookingId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `/bookings/${bookingId}/options`;
        const response = await apiRequest<BookingOptionsListResponse>("GET", url);

        // Handle different response structures
        // API might return { data: options[] } or options[] directly
        const optionsData = response?.data || response;

        // Ensure we always return an array
        return Array.isArray(optionsData) ? optionsData : [];
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch booking options';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    enabled: !!bookingId
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}

// Create new booking option
export function useCreateBookingOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, data }: { bookingId: string; data: CreateBookingOptionRequest }) => {
      const url = `/bookings/${bookingId}/options`;
      return apiRequest<BookingOptionResponse>("POST", url, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['bookingOptions', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Update booking option with vendor response (only when status is Exploring)
export function useUpdateBookingOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, optionId, data }: {
      bookingId: string;
      optionId: string;
      data: UpdateBookingOptionRequest
    }) => {
      const url = `/bookings/${bookingId}/options/${optionId}/vendor-response`;
      return apiRequest<BookingOptionResponse>("PUT", url, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['bookingOptions', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Update booking status
export function useUpdateBookingStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, data }: { bookingId: string; data: UpdateBookingStatusRequest }) => {
      const url = `/bookings/${bookingId}/status`;
      return apiRequest<BookingWithOptionsResponse>("PUT", url, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Cancel booking
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, data }: { bookingId: string; data: CancelBookingRequest }) => {
      const url = `/bookings/${bookingId}/cancel`;
      return apiRequest<BookingWithOptionsResponse>("POST", url, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Confirm vendor booking
export function useConfirmVendorBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, data }: { bookingId: string; data: ConfirmVendorBookingRequest }) => {
      const url = `/bookings/${bookingId}/confirm-vendor`;
      return apiRequest<BookingWithOptionsResponse>("POST", url, data);
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: ['booking', variables.bookingId] });
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
    },
  });
}

// Get booking statistics for dashboard
export function useBookingStats(organizationId?: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['bookingStats', organizationId],
    queryFn: async () => {
      setLoading(true);
      setError(null);
      try {
        const url = `/bookings/stats${organizationId ? `?organizationId=${organizationId}` : ''}`;
        const response = await apiRequest<{
          totalBookings: number;
          pendingCount: number;
          negotiatingCount: number;
          awaitingApprovalCount: number;
          confirmedCount: number;
          cancelledCount: number;
          requiresAttentionCount: number;
          avgProcessingTime: number;
          successRate: number;
        }>("GET", url);
        return response;
      } catch (err: any) {
        const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch booking statistics';
        setError(errorMessage);
        throw new Error(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  });

  return {
    data: query.data,
    loading: loading || query.isLoading,
    error: error || (query.error ? String(query.error) : null),
    refetch: query.refetch
  };
}